import { useEffect, useState } from "react";

/**
 * L'attente de la rédaction, rendue utile — 28/09/2026.
 *
 * Le premier achat réel l'a montré : « la rédaction prend jusqu'à neuf
 * minutes » est honnête, mais neuf minutes devant une phrase fixe, sur un
 * téléphone, juste après avoir payé, c'est long — et c'est l'impatient qui
 * ferme, puis doute.
 *
 * Trois choses changent l'attente sans rien promettre de faux :
 *
 * - **ce qui avance** : les trois étapes que le serveur écrit déjà, cochées au
 *   fur et à mesure ;
 * - **le temps écoulé**, compté depuis l'ouverture de cet écran — jamais un
 *   temps restant, qu'on ne connaît pas et qu'un impatient surveillerait ;
 * - **quelque chose à faire** : le rapport commence par une chose à faire en
 *   quinze minutes, et ce qu'elle demande se prépare maintenant. L'attente
 *   devient le début du travail, pas un temps mort.
 */

export const ETAPES_REDACTION = [
  { cle: "redaction", libelle: "Écriture de vos dix actions" },
  { cle: "pdf", libelle: "Mise en page du document" },
  { cle: "rangement", libelle: "Rangement et lien de téléchargement" },
] as const;

export const PREPARATION = [
  "Ouvrez sur votre téléphone un assistant conversationnel gratuit. La liste à jour est sur la page Outils.",
  "Retrouvez un exemple récent de votre tâche la plus lourde — un relevé, un courriel, un compte rendu — et retirez-en noms et numéros.",
  "Bloquez quinze minutes aujourd'hui : votre rapport s'ouvre sur une chose à faire dans ce temps-là.",
] as const;

/** « 2 min 05 s », « 45 s ». */
export function dureeLisible(secondes: number): string {
  const s = Math.max(0, Math.floor(secondes));
  const min = Math.floor(s / 60);
  const reste = s % 60;
  return min === 0 ? `${reste} s` : `${min} min ${String(reste).padStart(2, "0")} s`;
}

export function AttenteRedaction({ etape, courriel }: { etape: string | null; courriel: boolean }) {
  const [debut] = useState(() => Date.now());
  const [maintenant, setMaintenant] = useState(() => Date.now());

  useEffect(() => {
    const minuteur = window.setInterval(() => setMaintenant(Date.now()), 1000);
    return () => window.clearInterval(minuteur);
  }, []);

  const rang = ETAPES_REDACTION.findIndex((e) => e.cle === etape);
  const courant = rang < 0 ? 0 : rang;

  return (
    <div className="attente" style={{ marginTop: "14px" }}>
      <ol className="attente-etapes" aria-label="Où en est votre rapport">
        {ETAPES_REDACTION.map((e, i) => (
          <li key={e.cle} data-etat={i < courant ? "fait" : i === courant ? "encours" : "avenir"}>
            <span aria-hidden="true">{i < courant ? "✓" : i === courant ? "●" : "○"}</span>{" "}
            {e.libelle}
            {i < courant && <span className="sr"> — terminé</span>}
            {i === courant && <span className="sr"> — en cours</span>}
          </li>
        ))}
      </ol>
      {/* Le chronomètre ne s'annonce pas : le relire chaque seconde aux lecteurs d'écran serait un vacarme. */}
      <p className="fine" aria-hidden="true">
        En cours depuis {dureeLisible((maintenant - debut) / 1000)}.
      </p>

      <div className="refer" style={{ marginTop: "12px", textAlign: "left" }}>
        <b>Pendant ce temps, préparez vos quinze premières minutes</b>
        <ul style={{ margin: "8px 0 0", paddingLeft: "18px" }}>
          {PREPARATION.map((p) => (
            <li key={p} style={{ marginBottom: "6px" }}>
              {p}
            </li>
          ))}
        </ul>
      </div>

      <p className="fine" style={{ marginTop: "12px" }}>
        La rédaction prend jusqu'à neuf minutes.
        {courriel ? " Le lien vous sera aussi envoyé par courriel." : ""} Vous pouvez fermer cette fenêtre :
        le bouton <b>Récupérer mon rapport</b>, sur l'accueil, vous ramènera ici.
      </p>
    </div>
  );
}

/**
 * Quand le rapport est prêt et que l'onglet est en arrière-plan, le titre de
 * l'onglet le dit. L'impatient est parti voir autre chose : c'est là qu'il
 * regarde. Aucun son, aucune notification demandée — rien qu'on n'ait pas
 * autorisé.
 */
export function useSignalerPret(pret: boolean): void {
  useEffect(() => {
    if (!pret) return;
    const avant = document.title;
    document.title = "✓ Votre rapport est prêt — STRATERA";
    const revenir = () => {
      if (!document.hidden) document.title = avant;
    };
    document.addEventListener("visibilitychange", revenir);
    return () => {
      document.removeEventListener("visibilitychange", revenir);
      document.title = avant;
    };
  }, [pret]);
}
